import {
  TableColumn,
  TableHeaderColumn,
} from '@alefeducation/ui-elements-library-core';
import React from 'react';
import { Clock } from '@alefeducation/ui-elements-library-icons';
import PropTypes from 'prop-types';
import { formatDate } from '../../helpers';
import { isLateSubmission } from '../../../../utils/isLateSubmission';
import { useCommonSectionItemElements } from '../useCommonSectionItemElements';
import ResubmissionList from '../../../common/ResubmissionList/ResubmissionList';
import CustomTooltip from '../../../common/CustomTooltip/CustomTooltip';
import { StudentNameWithAvatar } from './StudentNameWithAvatar/StudentNameWithAvatar';

export const studentColumn = (t, width = '298px') => ({
  key: 'name',
  name: 'Students',
  width,
  alignText: 'left',
  renderColumn: ({ row }) => (
    <TableColumn
      row={row}
      isFirstColumn={true}
      cellProps={{ height: '44px' }}
      textAlign="left"
    >
      <StudentNameWithAvatar
        avatar={row.avatar}
        studentName={row.name}
        studentUsername={row.username}
        studentId={row.id}
      />
    </TableColumn>
  ),
  renderHeaderColumn: rows => (
    <TableHeaderColumn isFirstColumn rows={rows} width={width} textAlign="left">
      <div className="bold-col-heading">{t('Students')}</div>
    </TableHeaderColumn>
  ),
  sortable: true,
  height: '44px',
  paddingLeft: '23px',
});

export const emptyColumn = (width = '160px', fixedWidth = false) => ({
  key: '',
  name: '',
  renderColumn: ({ row }) => (
    <TableColumn row={row} isFirstColumn={false}>
      {''}
    </TableColumn>
  ),
  renderHeaderColumn: rows => (
    <TableHeaderColumn
      isFirstColumn={false}
      rows={rows}
      width={fixedWidth ? width : ''}
    >
      {''}
    </TableHeaderColumn>
  ),
  height: '44px',
  width,
});

export const resubmissionColumn = (t, width = '160px') => ({
  key: 'resubmission',
  name: t('Resubmissions'),
  renderColumn: ({ row }) => {
    const resubmissionCount = row.resubmission
      ? row.resubmission.resubmissionCount
      : 0;
    const resubmissionList =
      resubmissionCount > 0
        ? row.resubmission.resubmissions.map((item, index) => ({
            number: index + 1,
            value: formatDate(new Date(item.createdOn)),
          }))
        : [];
    const resubmissionListTooltipContent = (
      <ResubmissionList
        title={t('Resubmissions')}
        subTitle={resubmissionCount}
        list={resubmissionList}
      />
    );
    return (
      <TableColumn row={row} isFirstColumn={false}>
        {resubmissionCount === 0 ? (
          <span data-tip={t('Not requested')}>{'--'}</span>
        ) : (
          <CustomTooltip content={resubmissionListTooltipContent}>
            {resubmissionCount}
          </CustomTooltip>
        )}
      </TableColumn>
    );
  },
  renderHeaderColumn: rows => (
    <TableHeaderColumn isFirstColumn={false} rows={rows} width={width}>
      <div className="col-heading resubmission-header">
        {t('Resubmissions')}
      </div>
    </TableHeaderColumn>
  ),
  alignText: 'center',
  width,
  height: '44px',
});

export const fileColumn = (attachmentRequired, t, width = '126px') => {
  if (!attachmentRequired) return emptyColumn(width, true);
  return {
    key: 'file',
    name: t('File'),
    renderColumn: ({ row }) => {
      const { attachmentField } = useCommonSectionItemElements(
        row,
        row.submission,
      );
      return (
        <TableColumn row={row} isFirstColumn={false}>
          <div className="align-center">
            {attachmentField({ withLabel: false })}
          </div>
        </TableColumn>
      );
    },
    renderHeaderColumn: rows => (
      <TableHeaderColumn isFirstColumn={false} rows={rows} width={width}>
        <div className="col-heading">{t('File')}</div>
      </TableHeaderColumn>
    ),
    alignText: 'center',
    width,
    height: '44px',
  };
};

export const evaluationDateColumn = (t, width = '160px') => ({
  key: 'evaluatedOn',
  name: t('Evaluated Date'),
  renderColumn: ({ row }) => {
    const hasEvaluationDate =
      row.submission &&
      row.submission.status !== 'PENDING' &&
      row.submission.evaluatedOn;
    const evaluationDate = hasEvaluationDate
      ? formatDate(new Date(row.submission.evaluatedOn))
      : '--';
    return (
      <TableColumn row={row} isFirstColumn={false}>
        <div className="date-cell">{evaluationDate}</div>
      </TableColumn>
    );
  },
  renderHeaderColumn: rows => (
    <TableHeaderColumn isFirstColumn={false} rows={rows} width={width}>
      <div className="bold-col-heading resubmission-header">
        {t('Evaluated Date')}
      </div>
    </TableHeaderColumn>
  ),
  alignText: 'center',
  width,
  height: '44px',
});

export const returnDateColumn = (t, width = '160px') => ({
  key: 'returnedDate',
  name: t('Returned Date'),
  renderColumn: ({ row }) => {
    const hasReturnedDate = row.submission && row.submission.returnedOn;
    const returnDate = hasReturnedDate
      ? formatDate(new Date(row.submission.returnedOn))
      : '--';
    return (
      <TableColumn row={row} isFirstColumn={false}>
        <div className="date-cell">{returnDate}</div>
      </TableColumn>
    );
  },
  renderHeaderColumn: rows => (
    <TableHeaderColumn isFirstColumn={false} rows={rows} width={width}>
      <div className="bold-col-heading resubmission-header">
        {t('Returned Date')}
      </div>
    </TableHeaderColumn>
  ),
  alignText: 'center',
  width,
  height: '44px',
});

export const submissionDateColumn = (dueOn, t, width = '160px') => ({
  key: 'submittedOn',
  name: t('Submission Date'),
  renderColumn: ({ row }) => {
    const hasSubmissionDate = row.submission && row.submission.submittedOn;
    const submissionDate = hasSubmissionDate
      ? formatDate(new Date(row.submission.submittedOn))
      : '--';
    return (
      <TableColumn row={row} isFirstColumn={false}>
        {hasSubmissionDate &&
        isLateSubmission(dueOn, row.submission.submittedOn) ? (
          <LateSubmissionBubble submittedOn={submissionDate} />
        ) : (
          <div className="date-cell">{submissionDate}</div>
        )}
      </TableColumn>
    );
  },
  renderHeaderColumn: rows => (
    <TableHeaderColumn isFirstColumn={false} rows={rows} width={width}>
      <div className="bold-col-heading resubmission-header">
        {t('Submitted Date')}
      </div>
    </TableHeaderColumn>
  ),
  alignText: 'center',
  width,
  height: '44px',
});
export const evaluationColumn = (t, width = '360px') => ({
  key: 'file',
  name: t('File'),
  renderColumn: ({ row }) => {
    const { gradeField } = useCommonSectionItemElements(row, row.submission);
    return (
      <TableColumn row={row} isFirstColumn={false}>
        <div className="align-center">{gradeField || '--'}</div>
      </TableColumn>
    );
  },
  renderHeaderColumn: rows => (
    <TableHeaderColumn isFirstColumn={false} rows={rows}>
      <div className="col-heading">{t('Evaluation')}</div>
    </TableHeaderColumn>
  ),
  alignText: 'center',
  width,
  height: '44px',
});
export const gradeColumn = (t, width = '360px') => ({
  key: 'file',
  name: t('File'),
  renderColumn: ({ row }) => {
    const { gradeField } = useCommonSectionItemElements(row, row.submission);
    return (
      <TableColumn row={row} isFirstColumn={false}>
        <div className="align-center">{gradeField}</div>
      </TableColumn>
    );
  },
  renderHeaderColumn: rows => (
    <TableHeaderColumn isFirstColumn={false} rows={rows}>
      <div className="col-heading">{t('Grade')}</div>
    </TableHeaderColumn>
  ),
  alignText: 'center',
  width,
  height: '44px',
});

export const LateSubmissionBubble = ({ submittedOn }) => (
  <div className="late-submission-bubble">
    <Clock fontSize={12} color={'#FF6D6D'} />
    {submittedOn}
  </div>
);

LateSubmissionBubble.propTypes = {
  submittedOn: PropTypes.string.isRequired,
};
